import { createSelector, Selector } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import { ConnectionStatus, CredentialsStatus } from 'store/connection'
import { configurationStoreSelector, connectionStoreSelector } from './storeSelectors'
import {
  AmountFormat,
  Application,
  AuthorisedUser,
  CallableType,
  CopyPasteFormat,
  DateFormat,
  DefaultCollateralIdentifierType,
  DirectionFormat,
  HaircutSign,
  HaircutType,
  LoginStatus,
  LogoutReason,
  OktaUserStatus,
  RfqType,
  SettlementVenue,
  TradingLimitType,
  UserStatus
} from 'api'
import { createShallowEqualSelector } from 'utils'

export function createConnectionStatusSelector(): Selector<GlobalState, ConnectionStatus> {
  return createSelector(connectionStoreSelector, connectionStore => connectionStore.connectionStatus)
}

export function createCredentialsStatusSelector(): Selector<GlobalState, CredentialsStatus> {
  return createSelector(connectionStoreSelector, connectionStore => connectionStore.credentialsStatus)
}

export interface CombinedStatus {
  credentialsStatus: CredentialsStatus
  connectionStatus: ConnectionStatus
}

export function createCombinedStatusSelector(): Selector<GlobalState, CombinedStatus> {
  return createSelector(connectionStoreSelector, connectionStore => {
    return {
      credentialsStatus: connectionStore.credentialsStatus,
      connectionStatus: connectionStore.connectionStatus
    }
  })
}

export interface ConnectionDetails {
  credentialsStatus: CredentialsStatus
  connectionStatus: ConnectionStatus
  connectedOnce: boolean
  connectionTimeout: number
  connectionEndpoint: string
}

export function createConnectionDetailsSelector(): Selector<GlobalState, ConnectionDetails> {
  return createSelector(connectionStoreSelector, configurationStoreSelector, (connectionStore, configurationStore) => {
    return {
      credentialsStatus: connectionStore.credentialsStatus,
      connectionStatus: connectionStore.connectionStatus,
      connectedOnce: connectionStore.connectedOnce,
      connectionTimeout: configurationStore.connectionTimeoutMS,
      connectionEndpoint: configurationStore.connectionEndpoint
    }
  })
}

export interface SignInDetails {
  username: string | null
}

export function createSignInDetailsSelector(): Selector<GlobalState, SignInDetails> {
  return createSelector(connectionStoreSelector, connectionStore => {
    return {
      username: connectionStore.lastUsername
    }
  })
}

const UNAUTHORISED_USER: AuthorisedUser = {
  id: '',
  username: '',
  firstName: '',
  lastName: '',
  status: UserStatus.SUSPENDED,
  phoneNumber: null,
  algoName: null,
  notes: null,
  canTrade: false,
  shortId: null,
  organisationId: '',
  permissions: [],
  groups: [],
  productRights: [],
  tradingEntityDetails: [],
  allTradingEntities: false,
  application: Application.TRADING_GATEWAY,
  loggedInInstant: new Date(),
  sessionId: 1,
  gatewayId: 1,
  loginCorrelationId: '',
  loginStatus: LoginStatus.NOT_LOGGED_IN,
  userPreference: {
    id: '',
    amountFormat: AmountFormat.ACTUAL,
    directionFormat: DirectionFormat.BUY_SELL,
    dateFormat: DateFormat.EU,
    defaultCallableType: CallableType.NONE,
    callablePeriodDaysDefault: null,
    counterPartyGridView: false,
    settlementVenueDefault: SettlementVenue.Bilateral,
    showCompetitionDefault: true,
    rfqDurationMinsDefault: 60,
    counterpartyGroups: [],
    otwDurationMinsDefault: 5,
    defaultInvestmentDecisionMaker: '',
    defaultExecutionDecisionMaker: '',
    defaultRfqType: RfqType.FIRM,
    defaultHaircutType: HaircutType.HAIRCUT,
    notificationsEnabled: true,
    playAudibleAlertOnNotification: true,
    defaultCollateralIdentifierType: DefaultCollateralIdentifierType.ISIN,
    haircutSign: HaircutSign.ACTUAL_SELL_SIDE,
    basketAllocationDurationMinsDefault: 60,
    copyPasteFormat: CopyPasteFormat.TABULAR,
    pinnedCollaterals: []
  },
  featureToggle: {},
  oktaDetailsLastUpdated: new Date(),
  oktaLastLogin: new Date(),
  oktaUserStatus: OktaUserStatus.ACTIVE,
  lastLoggedOutInstant: null,
  lastLogoutReason: LogoutReason.OTHER,
  rateLimiterLogoutInstants: [],
  sessionAttributes: [],
  tradingControlLimit: {
    tradingLimitType: TradingLimitType.NONE,
    limitsByCurrency: {}
  },
  lastGroups: []
}

export function createAuthorisedUserSelector(): Selector<GlobalState, AuthorisedUser> {
  return createSelector(connectionStoreSelector, connectionStore => connectionStore.authorisedUser || UNAUTHORISED_USER)
}

export const authorisedUserSelector = createAuthorisedUserSelector()

export const authorisedUserNameSelector = createSelector(authorisedUserSelector, authorisedUser => authorisedUser.username)

export const authorisedUserPermissionsSelector = createShallowEqualSelector(
  authorisedUserSelector,
  authorisedUser => authorisedUser.permissions
)
